import React, { useEffect, useRef, useState } from 'react';
import './MagicCursor.css';

const STORAGE_KEY = 'kra_magic_cursor_v1';
const PARTICLE_LIFETIME = 3000;
const MAX_SEGMENT_PARTICLES = 128;
const PALETTE = ['#c084fc', '#818cf8', '#38bdf8', '#22d3ee', '#facc15'];

function readPreference() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return { enabled: stored?.enabled === true };
  } catch {
    return { enabled: false };
  }
}

export default function MagicCursor() {
  const [preference, setPreference] = useState(readPreference);
  const [supported, setSupported] = useState(false);
  const canvasRef = useRef(null);
  const controlsRef = useRef(null);
  const previousPointerRef = useRef(null);
  const enabledRef = useRef(preference.enabled);
  enabledRef.current = preference.enabled;

  useEffect(() => {
    const pointer = window.matchMedia('(any-hover: hover) and (any-pointer: fine)');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      setSupported(pointer.matches && !motion.matches);
    };
    update();
    pointer.addEventListener('change', update);
    motion.addEventListener('change', update);
    return () => {
      pointer.removeEventListener('change', update);
      motion.removeEventListener('change', update);
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ enabled: preference.enabled })); } catch { /* Optional local preference. */ }
  }, [preference]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!supported || !canvas) return undefined;
    const context = canvas.getContext('2d');
    if (!context) return undefined;
    let particles = [];
    let frame = null;
    let width = 0;
    let height = 0;
    let particleLimit = 320;
    let pixelRatio = 1;

    const stopFrame = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      const hardwareFactor = .75 + .25 * Math.min((navigator.hardwareConcurrency || 4) / 8, 1);
      particleLimit = Math.min(420, Math.max(220, Math.round(width * height / 3800 * hardwareFactor)));
      pixelRatio = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(4000000 / Math.max(1, width * height)));
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      if (particles.length && frame === null) frame = requestAnimationFrame(draw);
    };
    const draw = (now) => {
      frame = null;
      context.clearRect(0, 0, width, height);
      let activeCount = 0;
      for (let index = 0; index < particles.length; index += 1) {
        const particle = particles[index];
        const age = now - particle.born;
        if (age >= PARTICLE_LIFETIME) continue;
        particles[activeCount] = particle;
        activeCount += 1;
        const fadeProgress = Math.max(0, (age - 2000) / 1000);
        const size = particle.size * (age < 2000 ? 1 + Math.sin(age / 1000 * Math.PI) * .08 : 1 - fadeProgress);
        const alpha = age < 1000
          ? .92 - age / 1000 * .12
          : age < 2000
            ? .8 - (age - 1000) / 1000 * .28
            : .52 * (1 - fadeProgress) ** 2;
        const rotation = particle.rotation + age / PARTICLE_LIFETIME * .7;
        const centerX = particle.x + particle.dx * age / PARTICLE_LIFETIME;
        const centerY = particle.y + particle.dy * age / PARTICLE_LIFETIME;
        context.globalAlpha = alpha;
        context.fillStyle = PALETTE[particle.colorIndex];
        context.shadowColor = PALETTE[particle.colorIndex];
        context.shadowBlur = (particle.shape === 1 ? 3 : 0) * (1 - fadeProgress);
        context.beginPath();
        if (particle.shape === 0) {
          context.arc(centerX, centerY, size, 0, Math.PI * 2);
        } else {
          for (let point = 0; point < 8; point += 1) {
            const angle = rotation + point * Math.PI / 4;
            const radius = point % 2 === 0 ? size : size * .3;
            const x = centerX + Math.cos(angle) * radius;
            const y = centerY + Math.sin(angle) * radius;
            if (point === 0) context.moveTo(x, y);
            else context.lineTo(x, y);
          }
          context.closePath();
        }
        context.fill();
      }
      particles.length = activeCount;
      context.globalAlpha = 1;
      context.shadowBlur = 0;
      if (particles.length) frame = requestAnimationFrame(draw);
    };
    const spawnParticle = (x, y, born) => {
      const isStar = Math.random() < .14;
      particles.push({
        x: x + (Math.random() - .5) * 4,
        y: y + (Math.random() - .5) * 4,
        dx: (Math.random() - .5) * 18,
        dy: (Math.random() - .5) * 18,
        born,
        size: isStar ? 2.4 + Math.random() * 1.6 : .7 + Math.random() * 1.5,
        shape: isStar ? 1 : 0,
        colorIndex: Math.random() < .12 ? 4 : Math.floor(Math.random() * 4),
        rotation: Math.random() * Math.PI,
      });
    };
    const move = (event) => {
      if (event.pointerType !== 'mouse') return;
      if (!enabledRef.current || document.hidden || (event.target instanceof Node && controlsRef.current?.contains(event.target))) {
        previousPointerRef.current = null;
        return;
      }
      const now = performance.now();
      const current = { x: event.clientX, y: event.clientY, time: now };
      const previous = previousPointerRef.current;
      previousPointerRef.current = current;
      if (!previous) {
        if (particles.length < particleLimit) spawnParticle(current.x, current.y, now);
      } else {
        const deltaX = current.x - previous.x;
        const deltaY = current.y - previous.y;
        const distance = Math.hypot(deltaX, deltaY);
        if (distance > 0) {
          const speed = distance / Math.max(1, now - previous.time);
          const spacing = Math.max(4.5, 13 - Math.sqrt(speed) * 3.2);
          const desiredCount = Math.min(MAX_SEGMENT_PARTICLES, Math.ceil(distance / spacing));
          const available = Math.max(0, particleLimit - particles.length);
          const spawnCount = Math.min(desiredCount, available);
          for (let index = 1; index <= spawnCount; index += 1) {
            const progress = index / spawnCount;
            spawnParticle(previous.x + deltaX * progress, previous.y + deltaY * progress, now);
          }
        }
      }
      if (frame === null) frame = requestAnimationFrame(draw);
    };
    const visibilityChange = () => {
      if (document.hidden) {
        previousPointerRef.current = null;
        stopFrame();
      }
      else if (particles.length && frame === null) frame = requestAnimationFrame(draw);
    };
    resize();
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', visibilityChange);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', visibilityChange);
      stopFrame();
      particles = [];
      context.clearRect(0, 0, width, height);
    };
  }, [supported]);

  if (!supported) return null;
  const status = `Magic Cursor ${preference.enabled ? 'On' : 'Off'}`;
  return (
    <>
      <canvas ref={canvasRef} className="magic-cursor-canvas" aria-hidden="true" />
      <div className="magic-cursor-controls" ref={controlsRef}>
        <button type="button" className={`magic-cursor-wand ${preference.enabled ? 'magic-cursor-on' : ''}`} aria-label={status} aria-pressed={preference.enabled} title={status} onClick={() => { previousPointerRef.current = null; setPreference((current) => ({ enabled: !current.enabled })); }}><span aria-hidden="true">{'\uD83E\uDE84'}</span><span className="magic-cursor-tooltip" role="tooltip">{status}</span></button>
      </div>
    </>
  );
}